import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Target, CurrencyCircleDollar } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { gameSystems, getSymbol } from '../data/gameSystems';
import { useApp } from '../context/AppContext';
import { useTranslation } from '../hooks/useTranslation';
import { toast } from 'sonner';

export const GoalModal = ({ isOpen, onClose, character, editGoal = null }) => {
  const { dispatch } = useApp();
  const { t, lang } = useTranslation();
  const [name, setName] = useState('');
  const [targetAmount, setTargetAmount] = useState('');
  const [currencyId, setCurrencyId] = useState('');
  
  const isEditing = !!editGoal;
  const system = character ? gameSystems[character.systemId] : null;
  
  useEffect(() => {
    if (editGoal) {
      setName(editGoal.name);
      setTargetAmount(editGoal.targetAmount.toString());
      setCurrencyId(editGoal.currencyId);
    } else {
      setName('');
      setTargetAmount('');
      setCurrencyId(system?.defaultCurrency || '');
    }
  }, [editGoal, isOpen, system]);
  
  const handleSubmit = () => {
    const amount = parseInt(targetAmount, 10);
    if (!name.trim() || !amount || amount <= 0 || !currencyId) return;
    
    if (isEditing) {
      dispatch({
        type: 'UPDATE_GOAL',
        payload: {
          characterId: character.id,
          goalId: editGoal.id,
          updates: { name: name.trim(), targetAmount: amount, currencyId }
        }
      });
      toast.success(t('save'));
    } else {
      dispatch({
        type: 'CREATE_GOAL',
        payload: {
          characterId: character.id,
          name: name.trim(),
          targetAmount: amount,
          currencyId
        }
      });
      toast.success(t('createGoal'));
    }
    
    handleClose();
  };
  
  const handleClose = () => {
    setName('');
    setTargetAmount('');
    setCurrencyId('');
    onClose();
  };
  
  if (!character || !system) return null;
  
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
          />
          
          {/* Modal */}
          <motion.div
            className="fixed bottom-0 left-0 right-0 z-50 bg-[var(--surface)] rounded-t-3xl p-6 max-w-md mx-auto"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[var(--primary)]/10 flex items-center justify-center">
                  <Target size={24} weight="fill" className="text-[var(--primary)]" />
                </div>
                <h2 className="text-xl font-bold text-[var(--text)]">
                  {isEditing ? t('editGoal') : t('createGoal')}
                </h2>
              </div>
              <button 
                onClick={handleClose}
                className="p-2 rounded-full hover:bg-[var(--background)] transition-colors"
                data-testid="close-goal-modal"
              >
                <X size={24} className="text-[var(--text-muted)]" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-[var(--text-muted)] mb-2">
                  {t('goalName')}
                </label>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('goalName')}
                  className="h-14 bg-[var(--background)] border-[var(--border)] text-[var(--text)]"
                  data-testid="goal-name-input"
                />
              </div>
              
              {/* Target Amount */}
              <div>
                <label className="block text-sm font-medium text-[var(--text-muted)] mb-2">
                  {t('targetAmount')}
                </label>
                <Input
                  type="number"
                  min="1"
                  value={targetAmount}
                  onChange={(e) => setTargetAmount(e.target.value)}
                  placeholder="0"
                  className="text-2xl font-bold h-14 bg-[var(--background)] border-[var(--border)] text-[var(--text)]"
                  data-testid="goal-amount-input"
                />
              </div>
              
              {/* Currency */}
              <div>
                <label className="block text-sm font-medium text-[var(--text-muted)] mb-2">
                  {t('coinType')}
                </label>
                <Select value={currencyId} onValueChange={setCurrencyId}>
                  <SelectTrigger 
                    className="h-14 bg-[var(--background)] border-[var(--border)] text-[var(--text)]"
                    data-testid="goal-currency-select-trigger"
                  >
                    <SelectValue placeholder={t('coinType')} />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
                    {system.currencies.map(currency => (
                      <SelectItem 
                        key={currency.id} 
                        value={currency.id}
                        className="text-[var(--text)]"
                        data-testid={`goal-currency-option-${currency.id}`}
                      >
                        <div className="flex items-center gap-2">
                          <CurrencyCircleDollar size={20} weight="fill" />
                          {lang === 'it' ? currency.nameIt : currency.name} ({getSymbol(currency, lang)})
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Submit */}
              <Button
                onClick={handleSubmit}
                disabled={!name.trim() || !targetAmount || parseInt(targetAmount, 10) <= 0 || !currencyId}
                className="w-full h-14 rounded-2xl text-lg font-semibold bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white transition-all"
                data-testid="save-goal-btn"
              >
                {isEditing ? t('save') : t('createGoal')}
              </Button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
